import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { trackEngagedVisit } from '../util/gtmUtils';

const ENGAGED_VISIT_SECONDS = 30;
const STORAGE_KEY = 'engagedVisitTracked';

const isCheckoutPath = pathname => pathname.endsWith('/checkout');

const hasTracked = () => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

const markTracked = () => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, 'true');
  } catch (e) {
    // Storage can be unavailable (private browsing, blocked cookies). The
    // in-memory timer still limits this to once per page load.
  }
};

/**
 * Fires a single engaged_visit event once the visitor has stayed on site past
 * the engagement threshold. Suppressed during checkout so the signal measures
 * browsing interest rather than purchase intent.
 *
 * Renders nothing. Must be mounted inside a Router, and only on the client.
 */
const EngagedVisitTracker = () => {
  const location = useLocation();
  const locationRef = useRef(location);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    if (hasTracked()) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      if (isCheckoutPath(locationRef.current.pathname)) {
        return;
      }
      markTracked();
      trackEngagedVisit(ENGAGED_VISIT_SECONDS);
    }, ENGAGED_VISIT_SECONDS * 1000);

    return () => window.clearTimeout(timerId);
  }, []);

  return null;
};

export default EngagedVisitTracker;
